import { account } from '../core/account.js';
import { device } from '../core/device.js';
import { t } from '../core/i18n.js';
import { escapeHtml } from '../core/utils.js';
import { currentTermsVersion } from '../core/terms.js';

/* Pantalla de términos de uso y privacidad: lo primero que se ve, en la web y en las apps. Resume lo esencial, enlaza los
   textos completos (y los enseña dentro, para la tele), exige marcar dos casillas y ofrece aceptar o no. Quien no acepta no
   entra: se le explica y puede volver a leer, salir (apps) o cerrar la sesión (con cuenta). */

const legalBase = () => (account && typeof account.baseUrl === 'function' ? account.baseUrl() : '') || '';

function link(path, label) {
    return `<a class="external" href="${escapeHtml(legalBase() + path)}" target="_blank" rel="noopener">${escapeHtml(label)}</a>`;
}

function links() {
    const terms = (account.terms && account.terms.url) || '/legal/terms.html';
    const privacy = (account.terms && account.terms.privacyUrl) || '/legal/privacy.html';
    return { terms: link(terms, t('terms.linkTerms')), privacy: link(privacy, t('terms.linkPrivacy')), termsUrl: legalBase() + terms, privacyUrl: legalBase() + privacy };
}

function check(id, html) {
    return `<button type="button" class="terms-check" role="checkbox" aria-checked="false" data-check="${id}" data-nav-row>
        <span class="terms-box mi" aria-hidden="true">check_box_outline_blank</span>
        <span class="terms-check-text">${html}</span>
    </button>`;
}

/** Texto legal completo: se trae de las páginas /legal (mismo sitio o servidor de la app) y se queda con títulos y párrafos. */
async function loadLegal(url) {
    const res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error('http ' + res.status);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const main = doc.querySelector('main') || doc.body;
    const parts = [];
    main.querySelectorAll('h1, h2, p, li').forEach((el) => {
        if (el.closest('.prov, .lang, nav')) return;
        const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
        if (!text) return;
        const tag = el.tagName === 'H1' ? 'h3' : el.tagName === 'H2' ? 'h4' : 'p';
        parts.push(`<${tag}>${escapeHtml(text)}</${tag}>`);
    });
    if (!parts.length) throw new Error('empty');
    return parts.join('');
}

export default {
    path: '/terms/',
    nav: null,

    render() {
        const l = links();
        return `<div class="page no-nav" data-name="terms">
            <div class="page-content terms" data-terms>
                <section class="terms-card" data-step="read">
                    <header class="terms-head">
                        <img src="img/logo.png" alt=""/>
                        <div>
                            <div class="terms-kicker">${escapeHtml(t('terms.gate.kicker'))}</div>
                            <h1 class="terms-title">${escapeHtml(t('terms.gate.title'))}</h1>
                        </div>
                    </header>
                    <p class="terms-intro">${escapeHtml(t('terms.gate.intro'))}</p>
                    <ul class="terms-points">
                        <li><span class="mi">tune</span><span>${escapeHtml(t('terms.gate.p1'))}</span></li>
                        <li><span class="mi">home</span><span>${escapeHtml(t('terms.gate.p2'))}</span></li>
                        <li><span class="mi">privacy_tip</span><span>${escapeHtml(t('terms.gate.p3'))}</span></li>
                    </ul>
                    <div class="terms-full">
                        <button type="button" class="btn btn-ghost terms-full-toggle" data-full-toggle data-nav-row><span class="mi">description</span><span data-full-label>${escapeHtml(t('terms.gate.readFull'))}</span></button>
                        <div class="terms-full-body" data-full hidden tabindex="0"></div>
                    </div>
                    <div class="terms-checks">
                        ${check('1', t('terms.gate.check1', { terms: l.terms, privacy: l.privacy }))}
                        ${check('2', escapeHtml(t('terms.gate.check2')))}
                    </div>
                    <p class="terms-hint" data-hint>${escapeHtml(t('terms.gate.hint'))}</p>
                    <div class="terms-actions" data-nav-row>
                        <button type="button" class="btn btn-block terms-decline" data-decline>${escapeHtml(t('terms.decline'))}</button>
                        <button type="button" class="btn btn-accent btn-lg btn-block terms-accept" data-accept disabled aria-disabled="true"><span class="mi">check</span><span data-accept-label>${escapeHtml(t('terms.accept'))}</span></button>
                    </div>
                    <p class="terms-version">${escapeHtml(t('terms.gate.version', { version: currentTermsVersion(account) }))}</p>
                </section>
                <section class="terms-card terms-declined" data-step="declined" hidden>
                    <span class="mi terms-declined-icon">block</span>
                    <h2 class="terms-title">${escapeHtml(t('terms.gate.declinedTitle'))}</h2>
                    <p class="terms-intro">${escapeHtml(t('terms.gate.declinedText'))}</p>
                    <div class="terms-actions" data-nav-row>
                        <button type="button" class="btn btn-accent btn-lg btn-block" data-back-to-read data-nav-default><span class="mi">menu_book</span>${escapeHtml(t('terms.gate.backToRead'))}</button>
                        ${device.isCordova || device.isElectron || device.isTV ? `<button type="button" class="btn btn-block" data-exit><span class="mi">logout</span>${escapeHtml(t('terms.gate.exit'))}</button>` : ''}
                        ${account.signedIn ? `<button type="button" class="btn btn-ghost btn-block" data-signout><span class="mi">person_off</span>${escapeHtml(t('terms.gate.signOut'))}</button>` : ''}
                    </div>
                </section>
            </div>
        </div>`;
    },

    mount(page) {
        const root = page.el;
        const read = root.querySelector('[data-step="read"]');
        const declined = root.querySelector('[data-step="declined"]');
        const accept = root.querySelector('[data-accept]');
        const acceptLabel = root.querySelector('[data-accept-label]');
        const hint = root.querySelector('[data-hint]');
        const checks = Array.from(root.querySelectorAll('[data-check]'));
        const full = root.querySelector('[data-full]');
        const fullToggle = root.querySelector('[data-full-toggle]');
        const fullLabel = root.querySelector('[data-full-label]');
        let busy = false;
        let fullLoaded = false;

        const allChecked = () => checks.every((c) => c.getAttribute('aria-checked') === 'true');
        const refresh = () => {
            const ok = allChecked();
            accept.disabled = !ok || busy;
            accept.setAttribute('aria-disabled', String(!ok || busy));
            hint.hidden = ok;
        };
        checks.forEach((c) => c.addEventListener('click', (e) => {
            if (e.target.closest('a')) return; // los enlaces a los textos no marcan la casilla
            const on = c.getAttribute('aria-checked') !== 'true';
            c.setAttribute('aria-checked', String(on));
            c.querySelector('.terms-box').textContent = on ? 'check_box' : 'check_box_outline_blank';
            c.classList.toggle('is-on', on);
            refresh();
        }));

        fullToggle.addEventListener('click', async () => {
            const show = full.hidden;
            full.hidden = !show;
            fullLabel.textContent = t(show ? 'terms.gate.hideFull' : 'terms.gate.readFull');
            if (!show || fullLoaded) return;
            fullLoaded = true;
            full.innerHTML = `<p class="terms-loading"><span class="spinner"></span>${escapeHtml(t('terms.gate.loading'))}</p>`;
            const l = links();
            try {
                const [termsHtml, privacyHtml] = await Promise.all([loadLegal(l.termsUrl), loadLegal(l.privacyUrl)]);
                if (!root.isConnected) return;
                full.innerHTML = termsHtml + '<hr/>' + privacyHtml;
            } catch (e) {
                if (!root.isConnected) return;
                fullLoaded = false;
                full.innerHTML = `<p class="terms-loading">${t('terms.gate.loadFail', { terms: l.terms, privacy: l.privacy })}</p>`;
            }
        });

        const showStep = (which) => {
            read.hidden = which !== 'read';
            declined.hidden = which !== 'declined';
            const focus = which === 'read' ? accept.disabled ? checks[0] : accept : declined.querySelector('[data-back-to-read]');
            if (window.IPTV && window.IPTV.tvnav && (device.isTV || document.documentElement.classList.contains('mode-tv'))) setTimeout(() => window.IPTV.tvnav.focus(focus), 60);
            else if (focus && focus.focus) setTimeout(() => focus.focus(), 60);
        };

        root.querySelector('[data-decline]').addEventListener('click', () => showStep('declined'));
        root.querySelector('[data-back-to-read]').addEventListener('click', () => showStep('read'));
        const exitBtn = root.querySelector('[data-exit]');
        if (exitBtn) exitBtn.addEventListener('click', () => { if (navigator.app && navigator.app.exitApp) navigator.app.exitApp(); else window.close(); });
        const signOutBtn = root.querySelector('[data-signout]');
        if (signOutBtn) signOutBtn.addEventListener('click', () => window.IPTV.actions.signOut());

        accept.addEventListener('click', async () => {
            if (busy || !allChecked()) return;
            busy = true;
            refresh();
            acceptLabel.textContent = t('terms.gate.accepting');
            try {
                await window.IPTV.actions.acceptTerms();
            } catch (e) {
                busy = false;
                acceptLabel.textContent = t('terms.accept');
                refresh();
                window.IPTV.notify.error(t('common.errorNetwork'));
            }
        });

        refresh();
        showStep('read');
        return () => {};
    }
};
